import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  FormControl,
  MenuItem,
  Select,
  Alert,
  AlertTitle
} from "@mui/material";
import { images } from "../../assets";
import AppLayout from "../../components/layout";
import debugLogger from "../../utils/debug";
import { useLanguage } from "../../contexts/LanguageContext";

const CameraPermission = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialLanguage = location?.state?.language || "en";
  const sessionId = location?.state?.sessionId;
  const { language, setLanguage, t } = useLanguage();
  const [isRetesting, setIsRetesting] = useState(false);

  // Sync language from location state if provided
  useEffect(() => {
    if (initialLanguage && initialLanguage !== language) {
      setLanguage(initialLanguage);
    }
  }, [initialLanguage, language, setLanguage]);

  // Debug component mount
  useEffect(() => {
    debugLogger.componentMount('CameraPermission', {
      sessionId,
      initialLanguage,
      userAgent: navigator.userAgent
    });
    debugLogger.browserInfo();

    return () => {
      debugLogger.componentUnmount('CameraPermission');
    };
  }, [initialLanguage, sessionId]);

  // Detect browser type for specific instructions
  const getBrowserType = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      return 'chrome';
    } else if (userAgent.includes('firefox')) {
      return 'firefox';
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      return 'safari';
    } else if (userAgent.includes('edg')) {
      return 'edge';
    }
    return 'generic';
  };

  const browserType = getBrowserType();
  const browserInstructions = t.cameraPermission[browserType];

  // Test camera permissions
  const testCameraPermissions = async () => {
    debugLogger.cameraAccess('request', { action: 'retesting permissions' });
    setIsRetesting(true);

    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        debugLogger.cameraAccess('error', { reason: 'MediaDevices API not supported' });
        setIsRetesting(false);
        return false;
      }

      // Try to access camera
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });

      // Stop the stream immediately as we just wanted to test access
      stream.getTracks().forEach(track => track.stop());

      debugLogger.cameraAccess('granted', {
        reason: 'Camera access successful on retest',
        streamActive: true
      });

      setIsRetesting(false);
      return true;

    } catch (error) {
      debugLogger.cameraAccess('denied', {
        error: error.name,
        message: error.message,
        action: 'retest failed'
      });

      setIsRetesting(false);
      return false;
    }
  };

  // Handle "I've fixed it" button click
  const handleFixedClick = async () => {
    const hasAccess = await testCameraPermissions();
    
    if (hasAccess) {
      debugLogger.log('Camera access granted, redirecting back to verification', { sessionId });
      // Redirect back to the verification process
      if (sessionId) {
        navigate(`/process/${sessionId}`, {
          state: {
            language: language,
            cameraRetested: true
          }
        });
      } else {
        navigate('/verification', {
          state: {
            language: language,
            cameraRetested: true
          }
        });
      }
    } else {
      debugLogger.log('Camera access still denied after retest');
      // Show an alert or message that camera is still not accessible
      alert(t.cameraPermission.description);
    }
  };

  // Handle back button
  const handleBackClick = () => {
    debugLogger.navigation('CameraPermission', 'back', { sessionId });
    if (sessionId) {
      navigate(`/verification/${sessionId}`, {
        state: { language }
      });
    } else {
      navigate('/', {
        state: { language }
      });
    }
  };

  return (
    <AppLayout>
      <Box sx={{ backgroundColor: "#f5f5f7", minHeight: "100vh" }}>
        {/* Language Selector - positioned to work with Help Icon */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          sx={{ px: 2, pt: 1, position: 'relative', zIndex: 9997 }}
        >
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              displayEmpty
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="es">Español</MenuItem>
              <MenuItem value="it">Italiano</MenuItem>
              <MenuItem value="fr">Français</MenuItem>
              <MenuItem value="de">Deutsch</MenuItem>
              <MenuItem value="pt">Português</MenuItem>
              <MenuItem value="ru">Русский</MenuItem>
              <MenuItem value="zh">中文</MenuItem>
              <MenuItem value="ja">日本語</MenuItem>
              <MenuItem value="ko">한국어</MenuItem>
              <MenuItem value="ar">العربية</MenuItem>
              <MenuItem value="hi">हिन्दी</MenuItem>
              <MenuItem value="nl">Nederlands</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Logo */}
        <Box
          display="flex"
          justifyContent="center"
          sx={{ pt: { xs: 1, md: 1 } }}
        >
          <img
            src={images.certageLogo}
            alt="Logo"
            style={{
              width: "min(70vw, 300px)",
              height: "auto",
              maxHeight: "140px",
            }}
          />
        </Box>

        <Container
          maxWidth="lg"
          sx={{ px: { xs: 1, sm: 2, md: 4 }, py: 1 }}
        >
          {/* Title */}
          <Typography
            textAlign="center"
            fontFamily="Inter, sans-serif"
            color="#3F4145"
            fontWeight={800}
            fontSize={{ xs: 24, md: 36 }}
            lineHeight={{ xs: "32px", md: "48px" }}
            sx={{ mt: { xs: 2, md: 3 } }}
          >
            {t.cameraPermission.title}
          </Typography>

          {/* Subtitle */}
          <Typography
            textAlign="center"
            fontFamily="Inter, sans-serif"
            color="#777"
            fontWeight={600}
            fontSize={{ xs: 16, md: 20 }}
            lineHeight={{ xs: "22px", md: "28px" }}
            sx={{ mt: { xs: 1, md: 2 } }}
          >
            {t.cameraPermission.subtitle}
          </Typography>

          {/* Main Content */}
          <Box sx={{ mt: { xs: 3, md: 4 }, maxWidth: "800px", mx: "auto" }}>
            {/* Alert */}
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle sx={{ fontWeight: 600 }}>
                {t.cameraPermission.title}
              </AlertTitle>
              {t.cameraPermission.description}
            </Alert>

            {/* Instructions */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: "14px",
                border: "1px solid #D4ECFF",
                background: "#FBFDFF",
                mb: 3
              }}
            >
              <Typography
                variant="h6"
                fontFamily="Inter, sans-serif"
                color="#3F4145"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                {t.cameraPermission.instructions}
              </Typography>

              {/* Browser-specific instructions */}
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0"
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontFamily="Inter, sans-serif"
                  color="#1818de"
                  fontWeight={600}
                  sx={{ mb: 1 }}
                >
                  {browserInstructions.title}
                </Typography>

                <List dense>
                  {browserInstructions.steps.map((step, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={`${index + 1}. ${step}`}
                        primaryTypographyProps={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: "14px",
                          color: "#555"
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Paper>

            {/* Action Buttons */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              gap={2}
              justifyContent="center"
              sx={{ mt: 3 }}
            >
              <Button
                variant="contained"
                onClick={handleFixedClick}
                disabled={isRetesting}
                sx={{
                  backgroundColor: "#1818de",
                  px: { xs: 4, sm: 6 },
                  py: 1.5,
                  borderRadius: "12px",
                  fontSize: { xs: 16, sm: 18 },
                  fontWeight: 600,
                  color: "#fff",
                  "&:hover": { backgroundColor: "#1515b0" },
                  "&:disabled": { backgroundColor: "#ccc" }
                }}
              >
                {isRetesting ? "Testing..." : t.cameraPermission.fixedButton}
              </Button>

              <Button
                variant="outlined"
                onClick={handleBackClick}
                sx={{
                  borderColor: "#1818de",
                  color: "#1818de",
                  px: { xs: 4, sm: 6 },
                  py: 1.5,
                  borderRadius: "12px",
                  fontSize: { xs: 16, sm: 18 },
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#1515b0",
                    color: "#1515b0",
                    backgroundColor: "rgba(24, 24, 222, 0.04)"
                  }
                }}
              >
                {t.cameraPermission.backButton}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </AppLayout>
  );
};

export default CameraPermission;